import { DialogComponent, DialogCloseEvent, DialogOpenEvent } from '@theme/dialog';
import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

class RealEaseNewsletterPopup extends DialogComponent {
  #controller;
  #timer;
  #elapsed = false;
  #engaged = false;
  #dismissed = false;
  #returnFocus;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#setup();
  }

  disconnectedCallback() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
    if (this.refs.dialog.open) removeTrapFocus();
    super.disconnectedCallback();
  }

  showDialog() {
    if (this.refs.dialog.open) return;
    this.#returnFocus = document.activeElement;
    super.showDialog();
  }

  #setup() {
    this.#controller?.abort();
    clearTimeout(this.#timer);
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    const result = this.querySelector('[data-newsletter-result]');
    if (result?.dataset.success === 'true') this.#save('realease.newsletter.subscribed', '1');
    document.addEventListener('click', this.#handleTrigger, { signal });
    this.addEventListener(DialogOpenEvent.eventName, this.#handleOpen, { signal });
    this.addEventListener(DialogCloseEvent.eventName, this.#handleDismiss, { signal });
    if (result?.dataset.errors === 'true') {
      this.showDialog();
      return;
    }
    if (this.dataset.designMode === 'true' || this.dataset.autoOpen !== 'true' || !this.#eligible()) return;
    this.#elapsed = false;
    this.#engaged = window.scrollY > 60;
    window.addEventListener('scroll', this.#handleScroll, { passive: true, signal });
    const seconds = Number(this.dataset.delay) || 10;
    this.#timer = setTimeout(() => {
      this.#elapsed = true;
      this.#maybeOpen();
    }, seconds * 1000);
  }

  #eligible() {
    if (this.#dismissed || this.dataset.signedUp === 'true') return false;
    if (this.querySelector('[data-newsletter-result]')?.dataset.success === 'true') return false;
    try {
      if (localStorage.getItem('realease.newsletter.subscribed') === '1') return false;
      const closedAt = Number(localStorage.getItem('realease.newsletter.closedAt'));
      return !closedAt || Date.now() - closedAt > 7 * 24 * 60 * 60 * 1000;
    } catch {
      return true;
    }
  }

  #save(key, value) {
    try {
      localStorage.setItem(key, value);
    } catch {}
  }

  #handleScroll = () => {
    if (window.scrollY > 60) this.#engaged = true;
    this.#maybeOpen();
  };

  #maybeOpen() {
    if (!this.#elapsed || !this.#engaged || !this.#eligible()) return;
    if (document.querySelector('dialog[open], details.re-menu[open]')) return;
    if (document.activeElement?.matches('input, textarea, select, [contenteditable="true"]')) return;
    clearTimeout(this.#timer);
    this.showDialog();
  }

  #handleTrigger = (event) => {
    if (!(event.target instanceof Element)) return;
    const trigger = event.target.closest('a[data-newsletter-open], a[href]');
    if (!(trigger instanceof HTMLAnchorElement)) return;
    const url = new URL(trigger.href, window.location.href);
    if (url.origin !== window.location.origin) return;
    if (!trigger.hasAttribute('data-newsletter-open') && !['#re-newsletter', '#re-newsletter-form'].includes(url.hash)) return;
    event.preventDefault();
    const menu = trigger.closest('details');
    if (menu) menu.open = false;
    clearTimeout(this.#timer);
    this.showDialog();
  };

  #handleOpen = () => {
    trapFocus(this.refs.dialog);
    this.refs.dialog.querySelector('button')?.focus();
  };

  #handleDismiss = () => {
    removeTrapFocus();
    if (this.#returnFocus instanceof HTMLElement && this.#returnFocus.isConnected) this.#returnFocus.focus({ preventScroll: true });
    this.#dismissed = true;
    clearTimeout(this.#timer);
    this.#save('realease.newsletter.closedAt', String(Date.now()));
  };
}

class RealEaseWelcomeCode extends Component {
  requiredRefs = ['code', 'status'];

  async copyCode() {
    const code = this.refs.code.textContent?.trim();
    if (!code) return;
    try {
      await navigator.clipboard.writeText(code);
      this.refs.status.textContent = this.dataset.copied;
    } catch {
      this.refs.status.textContent = this.dataset.copyFallback;
    }
  }
}

if (!customElements.get('realease-newsletter-popup')) customElements.define('realease-newsletter-popup', RealEaseNewsletterPopup);
if (!customElements.get('realease-welcome-code')) customElements.define('realease-welcome-code', RealEaseWelcomeCode);
